import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PrimaryButton } from '../button';
import { PrimaryTextField } from '../input';
import { FilterSelect } from '../select';

const PrimaryDialog = ({
  open,
  onClose,
  onSave,
  title,
  subtitle,
  fields = [],
  submitButtonText = 'Create',
  cancelButtonText = 'Cancel',
  loadingText = 'Creating...',
  initialValues = {},
  maxWidth = 'sm',
  submitButtonVariant = 'contained',
  children,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const initialData = {};
      fields.forEach((field) => {
        initialData[field.name] = initialValues[field.name] || field.defaultValue || '';
      });
      setFormData(initialData);
      setErrors({});
    }
    prevOpenRef.current = open;
  }, [open, fields, initialValues]);

  const handleChange = (fieldName) => (e) => {
    const newValue = e?.target?.value ?? '';
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const handleSelectChange = (fieldName) => (_, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newValue ?? '',
    }));
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const validateField = (field, value) => {
    if (field.required && !value?.trim()) {
      return field.errorMessage || `${field.label} is required`;
    }
    if (field.maxLength && value?.length > field.maxLength) {
      return `${field.label} must be ${field.maxLength} characters or less`;
    }
    if (field.validation && typeof field.validation === 'function') {
      const validationResult = field.validation(value);
      if (validationResult !== true) {
        return validationResult;
      }
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    fields.forEach((field) => {
      const value = formData[field.name];
      const error = validateField(field, value);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleClose = () => {
    const resetData = {};
    fields.forEach((field) => {
      resetData[field.name] = '';
    });
    setFormData(resetData);
    setErrors({});
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await onSave?.(formData);
      handleClose();
    } catch (error) {
      const errorResponse = error.response?.data;
      const errorMessage =
        errorResponse?.error_description ||
        errorResponse?.description ||
        errorResponse?.message ||
        error.message ||
        'Failed to save';

      const fieldWithError = fields.find((f) => f.name === 'name' || f.name === 'title');
      if (fieldWithError) {
        if (
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('duplicate')
        ) {
          setErrors((prev) => ({
            ...prev,
            [fieldWithError.name]: `A ${fieldWithError.label.toLowerCase()} with this name already exists`,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            [fieldWithError.name]: errorMessage,
          }));
        }
      }
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = String(formData[field.name] ?? '');
    const error = errors[field.name];
    const hasError = !!error;

    if (field.type === 'select' || field.type === 'autocomplete') {
      return (
        <Box key={field.name} sx={{ mb: field === fields[fields.length - 1] ? 0 : 2 }}>
          <Typography
            sx={{
              fontFamily: 'IBM Plex Sans, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 500,
              fontStyle: 'normal',
              lineHeight: 1.5,
              letterSpacing: '0%',
              color: '#383838',
              mb: 1,
            }}
          >
            {field.label}
            {field.required && <span style={{ color: '#ef4444' }}> *</span>}
          </Typography>
          <FilterSelect
            type={field.type || 'select'}
            value={value}
            onChange={handleSelectChange(field.name)}
            options={field.options || []}
            placeholder={field.placeholder}
            sx={{
              width: '100%',
              background: '#FFFFFF',
              '& .MuiAutocomplete-root': {
                width: '100%',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
              },
            }}
          />
          {hasError && (
            <Typography
              sx={{
                color: '#ef4444',
                fontSize: '0.8125rem',
                mt: 0.5,
                ml: 1.25,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box key={field.name} sx={{ mb: field === fields[fields.length - 1] ? 0 : 2 }}>
        <Typography
          sx={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 500,
            fontStyle: 'normal',
            lineHeight: 1.5,
            letterSpacing: '0%',
            color: '#383838',
            mb: 1,
          }}
        >
          {field.label}
          {field.required && <span style={{ color: '#ef4444' }}> *</span>}
        </Typography>
        <PrimaryTextField
          fullWidth
          placeholder={field.placeholder}
          value={value}
          onChange={handleChange(field.name)}
          error={hasError}
          helperText={error || field.helperText}
          multiline={field.multiline}
          rows={field.rows}
          type={field.inputType}
        />
      </Box>
    );
  };

  const isFormValid = () => {
    if (fields.length === 0) return true;
    return fields.every((field) => {
      if (field.required) {
        const value = formData[field.name];
        return value && value.trim();
      }
      return true;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0px 25px 50px -12px #00000040',
          background: '#FFFFFF',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontWeight: 500,
          fontStyle: 'normal',
          fontSize: '1.25rem',
          lineHeight: '30px',
          letterSpacing: '-0.45px',
          color: '#0A0A0A',
          pb: 0.5,
          mx: 1,
        }}
      >
        {title}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#0A0A0A',
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          m: 1,
        }}
      >
        {subtitle && (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '20px',
              letterSpacing: '-0.15px',
              color: '#4A5565',
              mb: fields.length > 0 ? 2 : 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {children || fields.map((field) => renderField(field))}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <PrimaryButton onClick={handleClose} variant="outlined">
          {cancelButtonText}
        </PrimaryButton>
        <PrimaryButton
          onClick={handleSave}
          variant={submitButtonVariant}
          disabled={!isFormValid() || saving}
          sx={
            submitButtonVariant === 'danger'
              ? {
                  color: '#FFFFFF',
                  background: '#ff5e5e',
                  '&:hover': {
                    background: '#e04e4e',
                  },
                  '&:disabled': {
                    background: '#ff5e5e',
                    opacity: 0.6,
                  },
                }
              : {}
          }
        >
          {saving ? loadingText : submitButtonText}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

PrimaryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['input', 'select', 'autocomplete', 'textarea']),
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      multiline: PropTypes.bool,
      rows: PropTypes.number,
      inputType: PropTypes.string,
      options: PropTypes.array,
      defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      maxLength: PropTypes.number,
      showCharCount: PropTypes.bool,
      helperText: PropTypes.string,
      errorMessage: PropTypes.string,
      validation: PropTypes.func,
    })
  ),
  submitButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  loadingText: PropTypes.string,
  initialValues: PropTypes.object,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  submitButtonVariant: PropTypes.oneOf(['contained', 'outlined', 'text', 'danger']),
  children: PropTypes.node,
};

export default PrimaryDialog;
